import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import StatusBadge from "../components/StatusBadge";
import { LoadingState, ErrorState } from "../components/StateBlocks";
import { formatEventDate, formatEventTimeRange } from "../utils/dateTime";
import { MAX_NOTE_LENGTH } from "../utils/validation";
import api, { ApiError } from "../utils/api";

export default function RsvpManage() {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(null);
  const [rsvp, setRsvp] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formValues, setFormValues] = useState({ teenCount: 1, gradeRange: "", note: "", mobile: "" });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get("token");
    if (urlToken) {
      setToken(urlToken);
      // Remove the raw token from the visible URL/history immediately so it
      // is never bookmarked or left behind in browser history.
      window.history.replaceState({}, "", "/rsvp/manage");
    }
  }, [searchParams]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .getManagedRsvp(token)
      .then((data) => {
        setRsvp(data.rsvp);
        setEvent(data.event);
        setFormValues({
          teenCount: data.rsvp.teenCount,
          gradeRange: data.rsvp.gradeRange || "",
          note: data.rsvp.note || "",
          mobile: data.rsvp.mobile || "",
        });
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : "We couldn't find that RSVP.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);
    try {
      const updated = await api.updateManagedRsvp(token, {
        teenCount: Number(formValues.teenCount),
        gradeRange: formValues.gradeRange || null,
        note: formValues.note || null,
        mobile: formValues.mobile || null,
      });
      setRsvp(updated.rsvp);
      setSaveMessage({ type: "success", text: "Your RSVP has been updated." });
    } catch (err) {
      setSaveMessage({
        type: "error",
        text: err instanceof ApiError ? err.message : "Unable to save changes.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleCancel() {
    setSaving(true);
    try {
      await api.cancelManagedRsvp(token);
      setRsvp((prev) => ({ ...prev, status: "CANCELED" }));
      setCancelConfirmOpen(false);
    } catch (err) {
      setSaveMessage({
        type: "error",
        text: err instanceof ApiError ? err.message : "Unable to cancel your RSVP.",
      });
    } finally {
      setSaving(false);
    }
  }

  if (!token) {
    return (
      <>
        <Seo title="Manage RSVP" />
        <h1>Manage your RSVP</h1>
        <p>
          Use the link from your confirmation email to view or edit your RSVP. If you can&rsquo;t find it,
          visit our <a href="/contact">contact page</a> for help.
        </p>
      </>
    );
  }

  if (loading) return <LoadingState label="Loading your RSVP\u2026" />;
  if (error) return <ErrorState message={error} />;
  if (!rsvp) return null;

  const isEditable = rsvp.status === "CONFIRMED" || rsvp.status === "WAITLISTED";

  return (
    <>
      <Seo title="Manage RSVP" />
      <h1>Manage your RSVP</h1>
      {event && (
        <section className="card">
          <StatusBadge status={rsvp.status} />
          <h2>{event.title}</h2>
          <p>{formatEventDate(event.startAt)}</p>
          <p>{formatEventTimeRange(event.startAt, event.endAt)}</p>
        </section>
      )}

      {saveMessage && (
        <div className={`alert alert-${saveMessage.type === "success" ? "success" : "error"}`} role="status">
          {saveMessage.text}
        </div>
      )}

      {!isEditable && (
        <div className="alert alert-info" role="status">
          This RSVP is {rsvp.status.toLowerCase()} and can no longer be edited.
        </div>
      )}

      {isEditable && (
        <form className="card" onSubmit={handleSave}>
          <h2>Edit your RSVP</h2>
          <div className="form-field">
            <label htmlFor="manage-teenCount"># of teens attending</label>
            <input
              id="manage-teenCount"
              type="number"
              min="1"
              max="10"
              value={formValues.teenCount}
              onChange={(e) => setFormValues((prev) => ({ ...prev, teenCount: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <label htmlFor="manage-gradeRange">Grade range</label>
            <input
              id="manage-gradeRange"
              type="text"
              value={formValues.gradeRange}
              onChange={(e) => setFormValues((prev) => ({ ...prev, gradeRange: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <label htmlFor="manage-mobile">Mobile number</label>
            <input
              id="manage-mobile"
              type="tel"
              value={formValues.mobile}
              onChange={(e) => setFormValues((prev) => ({ ...prev, mobile: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <label htmlFor="manage-note">Note for the organizer</label>
            <textarea
              id="manage-note"
              maxLength={MAX_NOTE_LENGTH}
              value={formValues.note}
              onChange={(e) => setFormValues((prev) => ({ ...prev, note: e.target.value }))}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving\u2026" : "Save changes"}
          </button>
        </form>
      )}

      {isEditable && (
        <section className="card">
          <h2>Cancel your RSVP</h2>
          <p>If your plans change, you can cancel your RSVP. This will free up your spot for the waitlist.</p>
          {!cancelConfirmOpen ? (
            <button type="button" className="btn btn-danger" onClick={() => setCancelConfirmOpen(true)}>
              Cancel my RSVP
            </button>
          ) : (
            <div role="alertdialog" aria-label="Confirm cancellation">
              <p>Are you sure you want to cancel this RSVP? This cannot be undone.</p>
              <button type="button" className="btn btn-danger" onClick={handleCancel} disabled={saving}>
                Yes, cancel my RSVP
              </button>{" "}
              <button type="button" className="btn btn-secondary" onClick={() => setCancelConfirmOpen(false)}>
                Never mind
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );
}
